import {Component, OnInit} from '@angular/core';
import {EditComponent} from "../../../_components/edit-component/edit-component";
import {IShard, Shard} from "../company-create/models/shard";
import {EditingServiceProvider} from "../../../_services/editing.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ShardHttpService} from "./services/shard-http.service";
import {Steps, StepsLabels, StepsList} from "./steps";
import {AppRoutes} from "../../../enums/routes";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {LegalType, LegalTypeLabels, LegalTypeList} from "../company-create/models/legal-type";
import {LegalFormDescription, LegalFormList} from "../company-create/models/legal-form";
import {SalutationLabels, SalutationList} from "../company-create/models/salutation";
import {EditComponentBaseComponent} from "../../../_components/edit-component-base/edit-component-base.component";
import {StepDirective} from "../../../_directives/edit-component/step.directive";
import {SegmentComponent} from "../../../_components/configuration/segment/segment.component";
import {InputFieldComponent} from "../../../_components/configuration/input-field/input-field.component";
import {FormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {NgOptionComponent, NgSelectComponent} from "@ng-select/ng-select";
import {NgSelectScrollAwareDirective} from "../../../_directives/ng-select-scroll-aware.directive";

@Component({
  selector: 'app-shard-edit',
  templateUrl: './shard-edit.component.html',
  imports: [
    EditComponentBaseComponent,
    StepDirective,
    SegmentComponent,
    InputFieldComponent,
    FormsModule,
    NgClass,
    NgOptionComponent,
    NgSelectComponent,
    // #236: MUST stay imported alongside the `[appendTo]`s in the template. The directive's selector is
    // `ng-select[appendTo]`, so it applies automatically — but only in a component that imports it, and a
    // missing import is silent (no compile error), leaving a portaled panel frozen while the page scrolls.
    NgSelectScrollAwareDirective
  ],
  styleUrl: './shard-edit.component.scss'
})
export class ShardEditComponent extends EditComponent<IShard> implements OnInit {
  protected editing: boolean = false;
  protected current: Steps = Steps.GeneralInformation;
  protected readonly StepsList = StepsList;
  protected readonly StepsLabels = StepsLabels;
  protected readonly LegalTypeList = LegalTypeList;
  protected readonly LegalTypeLabels = LegalTypeLabels;
  protected readonly LegalType = LegalType;
  protected readonly parseInt = parseInt;
  protected readonly LegalFormDescription = LegalFormDescription;
  protected readonly SalutationLabels = SalutationLabels;
  protected readonly LegalFormList = LegalFormList;
  protected readonly SalutationList = SalutationList;

  constructor(provider: EditingServiceProvider, router: Router,
              route: ActivatedRoute, private http: ShardHttpService,
              private gas: GlobalAlertService) {
    super(router, route, provider.getService<IShard>('IShard'), () => new Shard());
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params.step) {
        this.current = Steps[params.step as keyof typeof Steps];
      }

      if (params.id && !params.id.isNullOrWhitespace() && this.persistence.entity?.id !== params.id) {
        this.http.load(params.id).withLoadingIndicator().subscribe({
          next: shard => {
            this.persistence.edit(this.persistence.redirect ?? `/${AppRoutes.OwnerShards}`, true, shard);
            if (!this.persistence.entity) throw new Error("Entity is undefined. This should never happen.");
            this.persistence.entity = Shard.parse(this.persistence.entity);
          },
          error: (err) => {
            this.gas.createAndShow("Fehler", "Der Shard konnte nicht geladen werden.", AlertLevel.error);
            console.error(err);
          }
        })
      } else if (!params.id || params.id.isNullOrWhitespace()) {
        this.editing = false;
        // Only seed a fresh shard the first time. queryParams emits again on every
        // step switch (setCurrent() -> move() re-navigates) — re-running edit() with a
        // null entity would call the factory and replace the in-progress shard with an
        // empty one, discarding everything typed so far (same trap as company-create).
        if (!this.persistence.entity) {
          this.persistence.edit(
            this.persistence.redirect ?? `/${AppRoutes.OwnerShards}`,
            this.persistence.isEntryPoint,
            undefined, this.persistence.extras
          );
        }
      } else {
        this.editing = true;
        this.persistence.entity = Shard.parse(this.persistence.entity);
      }
    })
  }

  protected setCurrent(step: Steps): void {
    step = Math.max(Math.min(step, Math.pow(2, StepsList.length - 1)), 1)
    this.move({
      step: Steps[step]
    }).then()
  }

  protected save(): void {
    if (!this.persistence.isEntryPoint) {
      this.persistence.save()
      return;
    }

    if (!this.persistence.entity) {
      this.gas.createAndShow("Fehler", "Der Shard konnte nicht gespeichert werden.", AlertLevel.error);
      console.error("Entity is undefined. This should never happen.");
      return;
    }
    this.http.upsert(this.persistence.entity).withLoadingIndicator().subscribe({
      next: (shard) => {
        this.gas.createAndShow("Erfolgreich", "Der Shard wurde erfolgreich gespeichert.", AlertLevel.success);
        this.persistence.entity = shard;
        this.persistence.save();
      },
      error: (err) => {
        this.gas.createAndShow("Fehler", "Der Shard konnte nicht gespeichert werden.", AlertLevel.error);
        console.error(err);
      }
    })
  }

  protected cancel(): void {
    this.persistence.cancel();
  }
}
